import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import Layout from "../components/layout/Layout";
import { useControlCash } from "../store/controlCashStore";
import { useToast } from "../contexts/ToastContext";
import { usePersistedFormState } from "../hooks/usePersistedFormState";

// ============================================================================
// HELPERS
// ============================================================================
const getTodayDate = () => {
  const today = new Date();
  return today.toISOString().split("T")[0];
};

// Categorías = las que crea el usuario + "Ahorro" (flujo especial para aportar a metas)
const getCategories = (customCategories = []) => [
  ...(customCategories || []).map(c => ({ value: c.id, label: c.name })),
  { value: "savings", label: "Ahorro (aportar a meta)" }
];

const DAYS_OF_MONTH = Array.from({ length: 31 }, (_, i) => i + 1);

// ============================================================================
// COMPONENTE: PageHeader
// ============================================================================
const PageHeader = ({ isEditMode, onBack }) => {
  return (
    <div className="mb-8">
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium mb-4 transition-colors group"
        aria-label="Volver a detalle del mes"
      >
        <svg 
          className="w-5 h-5 transform group-hover:-translate-x-1 transition-transform" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        Volver
      </button>
      <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">
        {isEditMode ? "Editar gasto" : "Agregar gasto"}
      </h1>
      <p className="text-gray-600">Registra un gasto del mes</p>
    </div>
  );
};

// ============================================================================
// COMPONENTE: FormField
// ============================================================================
const FormField = ({ 
  label, 
  error, 
  required = false, 
  children,
  helperText 
}) => {
  return (
    <div className="mb-6">
      <label className="block text-sm font-semibold text-gray-700 mb-2">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {children}
      {error && (
        <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          {error}
        </p>
      )}
      {helperText && !error && (
        <p className="mt-2 text-sm text-gray-500 flex items-center gap-1">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {helperText}
        </p>
      )}
    </div>
  );
};

// ============================================================================
// COMPONENTE: ToggleSwitch
// ============================================================================
const ToggleSwitch = ({ enabled, onChange, label, description }) => {
  return (
    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border-2 border-gray-200 hover:border-blue-300 transition-colors">
      <div className="flex-1">
        <label className="block text-sm font-semibold text-gray-700 mb-1 cursor-pointer">
          {label}
        </label>
        {description && (
          <p className="text-xs text-gray-500">{description}</p>
        )}
      </div>
      <button
        type="button"
        onClick={() => onChange(!enabled)}
        className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
          enabled ? "bg-blue-600" : "bg-gray-300"
        }`}
        aria-label={label}
        aria-pressed={enabled}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-lg transition-transform ${
            enabled ? "translate-x-8" : "translate-x-1"
          }`}
        />
      </button>
    </div>
  );
};

// ============================================================================
// COMPONENTE: ExpenseForm
// ============================================================================
const ExpenseForm = ({ onSubmit, onCancel, isLoading, initialData, goals = [], categories = [], currency, formatAmount, storageKey }) => {
  const firstCategory = categories.find(c => c.value !== "savings")?.value || categories[0]?.value || "";
  const defaultState = {
    name: "",
    amount: "",
    category: firstCategory,
    date: getTodayDate(),
    note: "",
    isRecurring: false,
    dueDay: null,
    goalId: ""
  };

  const [formData, setFormData, clearPersisted, wasRestored] = usePersistedFormState(storageKey, defaultState);

  // Cargar datos iniciales si existen (modo edición) y no se restauró desde storage
  useEffect(() => {
    if (wasRestored) return;
    if (initialData) {
      const amountStr = typeof initialData.amount === "number"
        ? initialData.amount.toString()
        : String(initialData.amount ?? "");
      setFormData({
        name: initialData.name,
        amount: amountStr,
        category: initialData.category,
        date: initialData.date,
        note: initialData.note,
        isRecurring: initialData.isRecurring,
        dueDay: initialData.dueDay
      });
    } else if (categories.length > 0) {
      const defCat = categories.find(c => c.value !== "savings")?.value || categories[0]?.value;
      if (defCat) {
        setFormData(prev => {
          const isValid = categories.some(c => c.value === prev.category);
          return isValid ? prev : { ...prev, category: defCat };
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData, categories, wasRestored]);

  // Estado de errores
  const [errors, setErrors] = useState({});

  // Estado de campos tocados (para mostrar errores solo después de interacción)
  const [touched, setTouched] = useState({});

  // Validación en tiempo real
  useEffect(() => {
    const newErrors = {};

    if (touched.name && !formData.name.trim()) {
      newErrors.name = "El nombre del gasto es obligatorio";
    }

    if (touched.amount) {
      if (!formData.amount) {
        newErrors.amount = "El monto es obligatorio";
      } else if (parseFloat(formData.amount) <= 0) {
        newErrors.amount = "El monto debe ser mayor a 0";
      }
    }

    if (formData.isRecurring && touched.dueDay && !formData.dueDay) {
      newErrors.dueDay = "Selecciona el día de vencimiento";
    }

    if (formData.category === "savings" && touched.goalId && !formData.goalId) {
      newErrors.goalId = "Selecciona una meta para este ahorro";
    }

    setErrors(newErrors);
  }, [formData, touched]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Marcar todos los campos como tocados
    setTouched({
      name: true,
      amount: true,
      category: true,
      date: true,
      dueDay: formData.isRecurring,
      goalId: formData.category === "savings"
    });

    // Validar todo el formulario
    const validationErrors = {};

    if (!formData.name.trim()) {
      validationErrors.name = "El nombre del gasto es obligatorio";
    }

    if (!formData.amount) {
      validationErrors.amount = "El monto es obligatorio";
    } else if (parseFloat(formData.amount) <= 0) {
      validationErrors.amount = "El monto debe ser mayor a 0";
    }

    if (formData.isRecurring && !formData.dueDay) {
      validationErrors.dueDay = "Selecciona el día de vencimiento";
    }

    if (formData.category === "savings" && !formData.goalId) {
      validationErrors.goalId = "Selecciona una meta para este ahorro";
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    // Si no hay errores, limpiar persistencia y enviar el formulario
    clearPersisted();
    onSubmit({
      ...formData,
      amount: parseFloat(formData.amount)
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Nombre del gasto */}
      <FormField 
        label="Nombre del gasto" 
        required 
        error={touched.name && errors.name}
      >
        <input
          type="text"
          value={formData.name}
          onChange={(e) => handleChange("name", e.target.value)}
          onBlur={() => handleBlur("name")}
          placeholder="Ej: Netflix, Supermercado, Gasolina..."
          className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all ${
            touched.name && errors.name
              ? "border-red-300 focus:border-red-500 focus:ring-red-200"
              : "border-gray-200 focus:border-blue-500 focus:ring-blue-200"
          }`}
          disabled={isLoading}
        />
      </FormField>

      {/* Monto */}
      <FormField 
        label="Monto" 
        required 
        error={touched.amount && errors.amount}
      >
        <div className="relative">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold">
            {currency}
          </span>
          <input
            type="number"
            step="0.01"
            min="0"
            value={formData.amount}
            onChange={(e) => handleChange("amount", e.target.value)}
            onBlur={() => handleBlur("amount")}
            placeholder="0.00"
            className={`w-full pl-12 pr-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all ${
              touched.amount && errors.amount
                ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                : "border-gray-200 focus:border-blue-500 focus:ring-blue-200"
            }`}
            disabled={isLoading}
          />
        </div>
      </FormField>

      {/* Categoría */}
      <FormField 
        label="Categoría" 
        required 
        helperText={categories.filter(c => c.value !== "savings").length === 0 ? "Crea categorías en Gastos Recurrentes para clasificar tus gastos." : undefined}
      >
        <div className="relative">
          <select
            value={formData.category}
            onChange={(e) => handleChange("category", e.target.value)}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:border-blue-500 focus:ring-blue-200 transition-all appearance-none bg-white cursor-pointer"
            disabled={isLoading}
          >
            {categories.map((cat) => (
              <option key={cat.value} value={cat.value}>
                {cat.label}
              </option>
            ))}
          </select>
          <svg 
            className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </FormField>

      {/* Selector de Meta (solo si categoría es "Ahorro") */}
      {formData.category === "savings" && (
        <FormField 
          label="¿A qué meta quieres aportar?" 
          required
          error={errors.goalId}
          helperText={goals.length === 0 ? "No tienes metas creadas. Ve a 'Metas de Ahorro' para crear una." : "Selecciona la meta a la que quieres agregar este ahorro"}
        >
          <div className="relative">
            <select
              value={formData.goalId}
              onChange={(e) => handleChange("goalId", e.target.value)}
              onBlur={() => handleBlur("goalId")}
              className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all appearance-none bg-white cursor-pointer ${
                errors.goalId
                  ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                  : "border-gray-200 focus:border-blue-500 focus:ring-blue-200"
              }`}
              disabled={isLoading || goals.length === 0}
            >
              <option value="">Selecciona una meta...</option>
              {goals
                .filter(g => g.status === "ACTIVE")
                .map((goal) => (
                  <option key={goal.id} value={goal.id}>
                    {goal.name} (Meta: {formatAmount(goal.targetAmount)})
                  </option>
                ))}
            </select>
            <svg 
              className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </FormField>
      )}

      {/* Fecha */}
      <FormField label="Fecha" required>
        <input
          type="date"
          value={formData.date}
          onChange={(e) => handleChange("date", e.target.value)}
          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:border-blue-500 focus:ring-blue-200 transition-all"
          disabled={isLoading}
        />
      </FormField>

      {/* Nota (opcional) */}
      <FormField label="Nota" helperText="Agrega detalles adicionales (opcional)">
        <textarea
          value={formData.note}
          onChange={(e) => handleChange("note", e.target.value)}
          placeholder="Ej: Pago mensual, incluye descuento..."
          rows={3}
          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:border-blue-500 focus:ring-blue-200 transition-all resize-none"
          disabled={isLoading}
        />
      </FormField>

      {/* Switch: ¿Es gasto recurrente? */}
      <div className="mb-6">
        <ToggleSwitch
          enabled={formData.isRecurring}
          onChange={(value) => {
            handleChange("isRecurring", value);
            if (!value) {
              handleChange("dueDay", null);
            }
          }}
          label="¿Es gasto recurrente?"
          description="Se repetirá automáticamente cada mes"
        />
      </div>

      {/* Día de vencimiento (condicional) */}
      {formData.isRecurring && (
        <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-5 space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-gray-800 mb-1">Gasto recurrente</h4>
              <p className="text-sm text-gray-600 mb-4">
                Este gasto se repetirá todos los meses en el día que selecciones
              </p>
              
              <FormField 
                label="Día de vencimiento" 
                required 
                error={touched.dueDay && errors.dueDay}
              >
                <div className="relative">
                  <select
                    value={formData.dueDay || ""}
                    onChange={(e) => handleChange("dueDay", e.target.value ? parseInt(e.target.value) : null)}
                    onBlur={() => handleBlur("dueDay")}
                    className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all appearance-none bg-white cursor-pointer ${
                      touched.dueDay && errors.dueDay
                        ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                        : "border-gray-200 focus:border-blue-500 focus:ring-blue-200"
                    }`}
                    disabled={isLoading}
                  >
                    <option value="">Selecciona un día</option>
                    {DAYS_OF_MONTH.map((day) => (
                      <option key={day} value={day}>
                        Día {day}
                      </option>
                    ))}
                  </select>
                  <svg 
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </FormField>
            </div>
          </div>
        </div>
      )}

      {/* Botones de acción */}
      <div className="flex flex-col-reverse sm:flex-row gap-3 pt-6 border-t-2 border-gray-100">
        <button
          type="button"
          onClick={() => { clearPersisted(); onCancel(); }}
          disabled={isLoading}
          className="w-full sm:w-auto px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Cancelar y volver"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="w-full sm:flex-1 px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          aria-label="Guardar gasto"
        >
          {isLoading ? (
            <>
              <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Guardando...
            </>
          ) : (
            <>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Guardar gasto
            </>
          )}
        </button>
      </div>
    </form>
  );
};

// ============================================================================
// COMPONENTE PRINCIPAL: AddEditExpense
// ============================================================================
const AddEditExpense = ({ isEditMode = false }) => {
  const { id } = useParams(); // Para modo edición
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { state, dispatch, getExpenseById, toStorageAmount, toDisplayAmount, formatDisplayAmount } = useControlCash();
  const toast = useToast();
  
  const [isLoading, setIsLoading] = useState(false);
  const [monthId, setMonthId] = useState(null);
  const [initialData, setInitialData] = useState(null);

  // Obtener monthId y datos iniciales
  useEffect(() => {
    if (isEditMode && id) {
      // Modo edición: cargar expense existente
      const expense = getExpenseById(id);
      if (expense) {
        setMonthId(expense.monthId);
        setInitialData({
          name: expense.name,
          amount: toDisplayAmount(expense.amount),
          category: expense.category,
          date: expense.date || getTodayDate(),
          note: expense.note || "",
          isRecurring: expense.isFromRecurring || false,
          dueDay: expense.dueDay || null
        });
      }
    } else {
      // Modo creación: obtener monthId de query params
      const monthIdParam = searchParams.get("monthId");
      setMonthId(monthIdParam);
    }
  }, [id, isEditMode, searchParams, getExpenseById, toDisplayAmount]);

  const handleSubmit = (expenseData) => {
    setIsLoading(true);

    setTimeout(() => {
      // Si es categoría "Ahorro", crear una contribución en lugar de un gasto
      if (expenseData.category === "savings" && expenseData.goalId) {
        const amountToStore = toStorageAmount(expenseData.amount);
        dispatch({
          type: "ADD_CONTRIBUTION",
          payload: {
            goalId: expenseData.goalId,
            amount: amountToStore,
            date: expenseData.date,
            note: expenseData.note || `Aporte desde ${expenseData.name}`,
            monthId: monthId
          }
        });
        console.log("💰 Contribución creada a meta:", expenseData.goalId);
        
        setIsLoading(false);
        toast.success(`Ahorro registrado exitosamente!\n\nSe agregó ${formatDisplayAmount(amountToStore)} a tu meta de ahorro.`);
        
        if (monthId) {
          navigate(`/month/${monthId}`);
        } else {
          navigate("/");
        }
        return;
      }

      // Flujo normal para gastos
      if (isEditMode && id) {
        const amountToStore = toStorageAmount(expenseData.amount);
        dispatch({
          type: "UPDATE_EXPENSE",
          payload: {
            id,
            patch: {
              name: expenseData.name,
              amount: amountToStore,
              category: expenseData.category,
              date: expenseData.date,
              note: expenseData.note,
              dueDay: expenseData.isRecurring ? expenseData.dueDay : null
            }
          }
        });
        console.log("✏️ Gasto actualizado:", id);
      } else {
        const amountToStore = toStorageAmount(expenseData.amount);
        dispatch({
          type: "ADD_EXPENSE",
          payload: {
            monthId,
            name: expenseData.name,
            amount: amountToStore,
            category: expenseData.category,
            date: expenseData.date,
            note: expenseData.note,
            status: "PAID",
            dueDay: expenseData.isRecurring ? expenseData.dueDay : null,
            isFromRecurring: expenseData.isRecurring,
            recurringTemplateId: null,
            paidAt: new Date().toISOString()
          }
        });
        console.log("➕ Gasto creado");
      }

      setIsLoading(false);
      toast.success(`Gasto ${isEditMode ? "actualizado" : "guardado"} exitosamente!`);
      
      // Navegar de vuelta al detalle del mes
      if (monthId) {
        navigate(`/month/${monthId}`);
      } else {
        navigate("/");
      }
    }, 500);
  };

  const handleCancel = () => {
    if (monthId) {
      navigate(`/month/${monthId}`);
    } else {
      navigate("/");
    }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <PageHeader isEditMode={isEditMode} onBack={handleCancel} />

        {/* Card principal del formulario */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
          <ExpenseForm
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isLoading={isLoading}
            initialData={initialData}
            goals={state.goals}
            categories={getCategories(state.customCategories)}
            currency={state.currencySymbol ?? state.currency}
            formatAmount={formatDisplayAmount}
            storageKey={isEditMode && id ? `expense_edit_${id}` : `expense_add_${monthId || "new"}`}
          />
        </div>

        {/* Sticky button en mobile (opcional, para mejor UX) */}
        <div className="sm:hidden fixed bottom-0 left-0 right-0 p-4 bg-white border-t-2 border-gray-200 shadow-lg z-10">
          <button
            onClick={() => document.querySelector('form').requestSubmit()}
            disabled={isLoading}
            className="w-full px-6 py-4 bg-gradient-to-r from-green-500 to-green-600 text-white font-bold rounded-xl shadow-lg flex items-center justify-center gap-2 disabled:opacity-50"
            aria-label="Guardar gasto"
          >
            {isLoading ? (
              <>
                <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Guardando...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Guardar gasto
              </>
            )}
          </button>
        </div>
      </div>
    </Layout>
  );
};

export default AddEditExpense;
